import React, { useState } from 'react';
import {
	Formik
} from 'formik';
import PropTypes from 'prop-types';
import produce from 'immer';
import Interweave from 'interweave';
import FormField from '../formField';
import {
	StyledForm as StyledFormikForm, StyledErrorMessageBlock, StyledInfoMessage,
} from '../../../base/forms/formik';
import * as StyledForm from '../form.styles';
import {
	Columns, Column, H2, Button
} from '../../../base';
import ApartmentBox from '../../../apartmentBox';
import {
	fields,
	getInitialValues,
	getInitialActiveInputMapper,
	fieldTypeMapper,
	requiredValues,
	requiredFiles
} from './values';
import { validate, allRequiredFilesPresent } from './validation';
import FormFileField from '../formFileField';
import FormCheckBoxField from '../formCheckBoxField';
import { submitApplicationForm } from '../../../../actions/applicationActions';
import FormSubmissionInfo from '../formSubmissionInfo';
import { BASE_API_URL } from '../../../../constants/urls';
import useGet from '../../../../hooks/useGet';

const GrindavikForm = ({
	apartment,
	formIntroText,
	applicationReducer,
	authInfo,
	dispatch,
	additionalInfo
}) => {
	const {
		formSubmissionLoading,
		formSubmissionError,
		formSubmissionErrorMessage,
		formSubmissionErrorStatus,
		fileSubmissionLoading,
		fileSubmissionError,
		activeApplicationId,
		invalidFiles
	} = applicationReducer;
	const initialValues = getInitialValues(apartment, authInfo);
	const [activeMapper, setActiveMapper] = useState(getInitialActiveInputMapper(initialValues));
	const [files, setFiles] = useState({});
	const [filesMissing, setFilesMissing] = useState(false);
	let submitValidationActive = false;
	let submitValidationFailed = false;

	const { response } = useGet(`${BASE_API_URL}/zipcodes`);

	const onSubmit = (values, { setSubmitting }) => {
		setSubmitting(false);
		// Local file verification
		// Since they are not part of formik we have to verify
		// them separately
		const allFilesPresent = allRequiredFilesPresent(files);
		setFilesMissing(!allFilesPresent);
		if(!allFilesPresent) return;
		dispatch(submitApplicationForm(values, files, 'grindavik'));
	};

	const setActive = (key) => {
		setActiveMapper(produce((draft) => {
			draft[key] = true;
		}, activeMapper));
	};

	const handleActive = (key, values) => {
		setActiveMapper(produce((draft) => {
			draft[key] = values[key] && values[key].length > 0;
		}, activeMapper));
	};

	const onFiles = (newFiles, fieldName) => {
		const updatedFiles = produce((draft) => {
			draft[fieldName] = newFiles;
		}, files);
		setFiles(updatedFiles);
	};

	return (
		<StyledForm.Wrapper loading={formSubmissionLoading}>
			<Formik
				initialValues={initialValues}
				validate={validate}
				onSubmit={onSubmit}>
				{({
					values,
					errors,
					isSubmitting,
					isValidating,
				}) => (
					<StyledFormikForm>
						{submitValidationActive = submitValidationActive || (isSubmitting && isValidating)}
						{submitValidationFailed = !submitValidationFailed && submitValidationActive && !isValidating && !isSubmitting}
						<Columns>
							<Column tablet={6 / 12} desktopSmall={8 / 12}>
								<StyledForm.BasicInfoWrapper>
									<H2 marginBottom>Umsókn / Application / Wniosek</H2>
									<div>{formIntroText}</div>
									{submitValidationFailed && (
										<StyledErrorMessageBlock>
											<span>Gat ekki lagt inn umsókn, einhverjir reitir voru ekki á réttu formi.</span>
											<span>Could not sumblit application, some fields are incorrect</span>
											<span>Nie mogłem/am przesłać wniosku, niektóre pola miały nieprawidłowy format</span>
										</StyledErrorMessageBlock>
									)}
									<StyledForm.BasicInfoForm>
										{
											fields.basicInfo.map(field => (
												<FormField
													key={field.id}
													field={field}
													handleActive={handleActive}
													setActive={setActive}
													values={values}
													required={requiredValues[field.fieldName]}
													active={activeMapper[field.fieldName]} />
											))
										}
									</StyledForm.BasicInfoForm>
								</StyledForm.BasicInfoWrapper>
							</Column>
							<Column tablet={6 / 12} desktopSmall={4 / 12}>
								<StyledForm.ApartmentBoxWrapper>
									<ApartmentBox apartment={apartment} />
								</StyledForm.ApartmentBoxWrapper>
							</Column>
						</Columns>
						<StyledForm.RenterInfoForm>
							<H2 marginBottom>Leigjandi / Applicant / Najemca</H2>
							<Columns flexWrap="wrap">
								{
									fields.renterInfo.map((field) => {
										const Field = fieldTypeMapper[field.type];

										return (
											<Column tablet={1 / 2} key={field.id}>
												<Field
													key={field.id}
													field={field}
													handleActive={handleActive}
													setActive={setActive}
													values={values}
													required={requiredValues[field.fieldName]}
													options={response ? response.data : []}
													active={activeMapper[field.fieldName]} />
											</Column>
										);
									})
								}
							</Columns>
						</StyledForm.RenterInfoForm>
						<StyledForm.RenterInfoForm>
							<H2 marginBottom>Meðleigjandi / Co-tenant / Współlokator</H2>
							<Columns flexWrap="wrap">
								{
									fields.additionalRenterInfo.map(field => (
										<Column tablet={1 / 2} key={field.id}>
											<FormField
												field={field}
												handleActive={handleActive}
												setActive={setActive}
												values={values}
												required={requiredValues[field.fieldName]}
												active={activeMapper[field.fieldName]} />
										</Column>
									))
								}
							</Columns>
						</StyledForm.RenterInfoForm>
						<StyledForm.AdditionalInfoForm>
							<Interweave content={additionalInfo || ''} />
						</StyledForm.AdditionalInfoForm>
						<StyledForm.RenterInfoForm>
							<H2 marginBottom>Viðhengi / Attachments / Załącznik</H2>
							<StyledInfoMessage>Athugið! Ef setja á fleiri en eitt viðhengi í einn kassa: vinsamlegast veldu/dragðu allar skrár inn í einu</StyledInfoMessage>
							<StyledInfoMessage>Please note, if you wish to attach more documents, you must upload/drag them at the same time to the field below.</StyledInfoMessage>
							<StyledInfoMessage>Uwaga! Jeśli musisz załączyć więcej niż jeden załącznik: wybierz/przeciągnij wszystkie pliki na raz.</StyledInfoMessage>
							<Columns flexWrap="wrap">
								{
									fields.files.map(field => (
										<Column tablet={1 / 2} desktopSmall={1 / 3} key={field.id}>
											<FormFileField
												invalidFiles={invalidFiles}
												onFiles={onFiles}
												field={field}
												required={requiredFiles[field.fieldName]}
												values={values} />
										</Column>
									))
								}
							</Columns>
							{filesMissing && (
								<div>
									<StyledErrorMessageBlock>Nauðsynlegt er að skila inn stjörnumerktum viðhengjum</StyledErrorMessageBlock>
									<StyledErrorMessageBlock>Please attach all required documents</StyledErrorMessageBlock>
									<StyledErrorMessageBlock>Niezbędne jest przesłanie załączników oznaczonych gwiazdką</StyledErrorMessageBlock>
								</div>
							)}
						</StyledForm.RenterInfoForm>
						<StyledForm.CreditRatingWrapper>
							<FormCheckBoxField
								field={fields.approvals[0]}
								handleActive={handleActive}
								setActive={setActive}
								values={values}
								required={requiredValues[fields.approvals[0].fieldName]}
								active={activeMapper[fields.approvals[0].fieldName]} />
						</StyledForm.CreditRatingWrapper>
						<StyledForm.ButtonWrapper>
							<FormSubmissionInfo
								activeApplicationId={activeApplicationId}
								errors={errors}
								dispatch={dispatch}
								files={files}
								fileSubmissionLoading={fileSubmissionLoading}
								fileSubmissionError={fileSubmissionError}
								formSubmissionError={formSubmissionError}
								formSubmissionErrorMessage={formSubmissionErrorMessage}
								formSubmissionErrorStatus={formSubmissionErrorStatus} />
							<Button
									type="submit"
									primary
									large
									loading={isSubmitting || formSubmissionLoading}
									disabled={isSubmitting || formSubmissionLoading || fileSubmissionError}>
								<span>Sækja um íbúð</span>
							</Button>
						</StyledForm.ButtonWrapper>
					</StyledFormikForm>
					)}
			</Formik>
		</StyledForm.Wrapper>
	);
};

GrindavikForm.propTypes = {
	apartment: PropTypes.object.isRequired
};

export default GrindavikForm ;
